import React, { useEffect, useState } from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import api from '../api/axios';

interface AudioPlayerProps {
  attachmentId: string;
  fileUrl: string;
  filename: string;
  contentType?: string;
  headers: Record<string, string>;
}

export const AudioPlayer: React.FC<AudioPlayerProps> = ({
  attachmentId: _attachmentId,
  fileUrl,
  filename,
  contentType,
  headers
}) => {
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadAudio = async () => {
      try {
        setLoading(true);
        const response = await api.get(fileUrl, { headers });
        const downloadUrl = response.data.url;
        const isLocalUrl = downloadUrl.includes('localhost') || downloadUrl.includes('127.0.0.1');

        if (isLocalUrl) {
          const urlObj = new URL(downloadUrl);
          let relativePath = urlObj.pathname + urlObj.search;
          if (relativePath.startsWith('/api/')) {
            relativePath = relativePath.substring(5);
          }

          const fileResponse = await api.get(relativePath, {
            headers,
            responseType: 'blob'
          });

          const blob = new Blob([fileResponse.data], { type: contentType || 'audio/webm' });
          const url = window.URL.createObjectURL(blob);
          setAudioUrl(url);
        } else {
          setAudioUrl(downloadUrl);
        }
        setLoading(false);
      } catch (err) {
        console.error('Failed to load audio:', err);
        setError('Failed to load audio file');
        setLoading(false);
      }
    };

    loadAudio();

    // Cleanup blob URL on unmount
    return () => {
      if (audioUrl && audioUrl.startsWith('blob:')) {
        window.URL.revokeObjectURL(audioUrl);
      }
    };
  }, [fileUrl, contentType, headers]);

  if (loading) {
    return (
      <Box sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        p: 1.5,
        backgroundColor: 'grey.50',
        display: 'flex',
        alignItems: 'center',
        gap: 1
      }}>
        <CircularProgress size={20} />
        <Typography variant="caption" color="text.secondary">
          Loading audio...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{
        border: '1px solid',
        borderColor: 'error.main',
        borderRadius: 1,
        p: 1.5,
        backgroundColor: 'error.50'
      }}>
        <Typography variant="caption" color="error">
          {error}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{
      border: '1px solid',
      borderColor: 'divider',
      borderRadius: 1,
      p: 1.5,
      backgroundColor: 'grey.50'
    }}>
      <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 1 }}>
        {filename}
      </Typography>
      {audioUrl && (
        <audio
          controls
          style={{ width: '100%', maxWidth: '400px' }}
          src={audioUrl}
        />
      )}
    </Box>
  );
};